// acta_patch_commercial_visibility_gmao_map_v1
// acta_patch_settings_machine_tab_final_v1
// ACTA_SETTINGS_CONTEXTUAL_NAV_V3
import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Bell,
  Buildings,
  CheckCircle,
  EnvelopeSimple,
  Eye,
  EyeSlash,
  Key,
  Lock,
  Phone,
  ShieldCheck,
  User,
  UsersThree,
  Wrench,
} from "@phosphor-icons/react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import NotificationSettings
  from "@/pages/NotificationSettings";
import MachineCatalogSettings
  from "@/pages/MachineCatalogSettings";
import Users
  from "@/pages/Users";
import ShareMessageSettings
  from "@/components/settings/ShareMessageSettings";
import MyMailSettings
  from "@/components/settings/MyMailSettings";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useTenantBranding }
  from "@/context/TenantBrandingContext";
import {
  changePassword,
  updateMe,
} from "@/api/auth";
import * as tenantApi from "@/api/tenant";
import { formatApiError } from "@/lib/api";
import { hasPageAccess } from "@/lib/accessPermissions";


const API =
  process.env.REACT_APP_BACKEND_URL ||
  "";


const BASE_TABS = [
  {
    id: "profile",
    label: "Profil & compte",
    icon: User,
  },
  {
    id: "mail_account",
    label: "Ma messagerie",
    icon: EnvelopeSimple,
    mailOnly: true,
  },
  {
    id: "secure_share",
    label: "Partage sécurisé",
    icon: EnvelopeSimple,
  },
  {
    id: "security",
    label: "Sécurité",
    icon: ShieldCheck,
  },
  {
    id: "notifications",
    label: "Notifications",
    icon: Bell,
  },
  {
    id: "machine_catalog",
    label: "Catalogue machines",
    icon: Wrench,
    adminOnly: true,
  },
];


const ROLE_LABELS = {
  saas_admin: "SaaS Admin",
  admin: "Administrateur",
  manager: "Responsable commercial",
  user: "Commercial",
  technician: "Technicien",
  maintenance_manager:
    "Responsable maintenance",
};



function errorMessage(
  error,
  fallback
) {
  return (
    formatApiError(
      error?.response?.data?.detail
    ) ||
    error?.message ||
    fallback
  );
}



function SectionCard({
  title,
  description,
  icon: Icon,
  children,
  testId,
}) {
  return (
    <section
      className="lirah-card settings-section-card min-w-0 p-4 sm:p-6"
      data-testid={testId}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--do-accent-soft)] text-[var(--do-accent)]">
          <Icon
            size={20}
            weight="duotone"
          />
        </div>

        <div>
          <h2 className="font-heading text-base font-black text-[var(--do-ink)]">
            {title}
          </h2>

          <p className="mt-1 text-xs text-[var(--do-muted)]">
            {description}
          </p>
        </div>
      </div>

      <div className="mt-5">
        {children}
      </div>
    </section>
  );
}


export default function Settings() {
  const {
    user,
    refresh,
  } = useAuth();

  useTheme();

  const branding =
    useTenantBranding();

  const [
    searchParams,
    setSearchParams,
  ] = useSearchParams();

  const requestedTab =
    searchParams.get("section")
    || "profile";

  const [
    activeTab,
    setActiveTabState,
  ] = useState(requestedTab);

  const [
    email,
    setEmail,
  ] = useState("");

  const [
    phone,
    setPhone,
  ] = useState("");

  const [
    companyName,
    setCompanyName,
  ] = useState("");

  const [
    logoUrl,
    setLogoUrl,
  ] = useState("");

  const [
    logoFile,
    setLogoFile,
  ] = useState(null);

  const [
    currentPassword,
    setCurrentPassword,
  ] = useState("");

  const [
    newPassword,
    setNewPassword,
  ] = useState("");

  const [
    confirmPassword,
    setConfirmPassword,
  ] = useState("");

  const [
    showPasswords,
    setShowPasswords,
  ] = useState(false);

  const [
    saving,
    setSaving,
  ] = useState(false);


  useEffect(() => {
    setEmail(
      user?.email ||
      ""
    );

    setPhone(
      user?.phone ||
      ""
    );
  }, [user]);


  useEffect(() => {
    tenantApi
      .getBranding()
      .then(
        (data) => {
          setCompanyName(
            data?.name ||
            user?.tenant?.name ||
            ""
          );

          setLogoUrl(
            data?.logoUrl ||
            ""
          );
        }
      )
      .catch(
        () => {
          setCompanyName(
            user?.tenant?.name ||
            ""
          );
        }
      );
  }, [user]);


  const role =
    user?.role ||
    "user";

  const canManageTenant =
    role === "admin";

  const canUseMail =
    hasPageAccess(
      user,
      "mail",
      "mail"
    );

  const canManageUsers =
    [
      "admin",
      "manager",
      "maintenance_manager",
    ].includes(role);

  const settingsTabs =
    useMemo(
      () => {
        const visibleBaseTabs =
          BASE_TABS.filter(
            (tab) =>
              (
                !tab.adminOnly ||
                canManageTenant
              )
              && (
                !tab.mailOnly ||
                canUseMail
              )
          );

        if (!canManageUsers) {
          return visibleBaseTabs;
        }

        return [
          visibleBaseTabs[0],
          {
            id: "users",
            label:
              role ===
              "maintenance_manager"
                ? "Techniciens"
                : "Utilisateurs",
            icon: UsersThree,
          },
          ...visibleBaseTabs.slice(1),
        ];
      },
      [
        canManageTenant,
        canManageUsers,
        canUseMail,
        role,
      ]
    );


  useEffect(() => {
    const section =
      searchParams.get("section")
      || "profile";

    const sectionAllowed =
      settingsTabs.some(
        (tab) =>
          tab.id === section
      );

    const nextTab =
      sectionAllowed
        ? section
        : "profile";

    if (activeTab !== nextTab) {
      setActiveTabState(nextTab);
    }

    if (
      !sectionAllowed
      && searchParams.has(
        "section"
      )
    ) {
      const nextSearchParams =
        new URLSearchParams(
          searchParams
        );

      nextSearchParams.delete(
        "section"
      );

      setSearchParams(
        nextSearchParams,
        { replace: true }
      );
    }
  }, [
    activeTab,
    searchParams,
    setSearchParams,
    settingsTabs,
  ]);


  const saveProfile =
    async () => {
      setSaving(true);

      try {
        await updateMe({
          email,
          phone,
        });

        await refresh?.();

        toast.success(
          "Profil mis à jour."
        );
      } catch (error) {
        toast.error(
          errorMessage(
            error,
            "Impossible de modifier le profil."
          )
        );
      } finally {
        setSaving(false);
      }
    };


  const savePassword =
    async () => {
      if (
        !currentPassword ||
        !newPassword ||
        !confirmPassword
      ) {
        toast.error(
          "Tous les champs sont obligatoires."
        );

        return;
      }

      if (
        newPassword !==
        confirmPassword
      ) {
        toast.error(
          "Les mots de passe ne correspondent pas."
        );

        return;
      }

      setSaving(true);

      try {
        await changePassword(
          currentPassword,
          newPassword
        );

        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");

        toast.success(
          "Mot de passe modifié."
        );
      } catch (error) {
        toast.error(
          errorMessage(
            error,
            "Impossible de modifier le mot de passe."
          )
        );
      } finally {
        setSaving(false);
      }
    };


  const saveBranding =
    async () => {
      if (!canManageTenant) {
        toast.error(
          "Seul l’administrateur peut modifier l’identité du tenant."
        );

        return;
      }

      setSaving(true);

      try {
        await tenantApi.updateBranding({
          name: companyName,
        });

        if (logoFile) {
          const uploaded =
            await tenantApi.uploadLogo(
              logoFile
            );

          setLogoUrl(
            uploaded?.logoUrl ||
            ""
          );

          setLogoFile(null);
        }

        await branding?.refresh?.();

        toast.success(
          "Identité de la société mise à jour."
        );
      } catch (error) {
        toast.error(
          errorMessage(
            error,
            "Impossible de modifier l’identité."
          )
        );
      } finally {
        setSaving(false);
      }
    };


  return (
    <div
      className="acta-standard-page w-full min-w-0 max-w-full p-4 sm:p-5 lg:p-8"
      data-testid="account-settings-page"
    >
      {activeTab ===
        "profile" && (
        <div
          className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(min(24rem,100%),1fr))] gap-4"
          data-testid="settings-profile-panel"
        >
          <SectionCard
            title="Profil utilisateur"
            description="Informations personnelles du compte connecté."
            icon={User}
          >
            <div className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(min(13rem,100%),1fr))] gap-4">
              <label>
                <span className="text-xs font-semibold text-[var(--do-ink)]">
                  Adresse e-mail
                </span>

                <div className="relative mt-2">
                  <EnvelopeSimple
                    size={17}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--do-muted)]"
                  />

                  <input
                    value={email}
                    onChange={(event) =>
                      setEmail(
                        event.target.value
                      )
                    }
                    className="h-10 w-full rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] pl-10 pr-3 text-xs outline-none focus:border-[var(--do-accent)]"
                    data-testid="account-email-input"
                  />
                </div>
              </label>

              <label>
                <span className="text-xs font-semibold text-[var(--do-ink)]">
                  Téléphone / WhatsApp
                </span>

                <div className="relative mt-2">
                  <Phone
                    size={17}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--do-muted)]"
                  />

                  <input
                    value={phone}
                    onChange={(event) =>
                      setPhone(
                        event.target.value
                      )
                    }
                    placeholder="+33612345678"
                    className="h-10 w-full rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] pl-10 pr-3 text-xs outline-none focus:border-[var(--do-accent)]"
                    data-testid="account-phone-input"
                  />
                </div>
              </label>
            </div>

            <button
              type="button"
              onClick={saveProfile}
              disabled={saving}
              className="mt-5 inline-flex h-9 items-center gap-2 rounded-lg bg-[var(--do-blue)] px-4 text-xs font-heading font-bold text-[var(--do-on-accent)] disabled:opacity-50"
              data-testid="save-contact-btn"
            >
              <CheckCircle
                size={16}
                weight="fill"
              />

              Enregistrer le profil
            </button>
          </SectionCard>

          <SectionCard
            title="Informations du compte"
            description="Tenant, plan et rôle attribué."
            icon={Buildings}
          >
            <dl className="space-y-3 text-xs">
              <div className="flex justify-between gap-4">
                <dt className="text-[var(--do-muted)]">
                  Société
                </dt>

                <dd className="font-semibold text-[var(--do-ink)]">
                  {user?.tenant?.name ||
                    companyName ||
                    "—"}
                </dd>
              </div>

              <div className="flex justify-between gap-4">
                <dt className="text-[var(--do-muted)]">
                  Rôle
                </dt>

                <dd className="font-semibold text-[var(--do-ink)]">
                  {ROLE_LABELS[role] ||
                    role}
                </dd>
              </div>

              <div className="flex justify-between gap-4">
                <dt className="text-[var(--do-muted)]">
                  Plan
                </dt>

                <dd className="font-semibold text-[var(--do-ink)]">
                  {user?.tenant?.plan ||
                    user?.plan ||
                    "—"}
                </dd>
              </div>
            </dl>
          </SectionCard>

          {canManageTenant && (
            <SectionCard
              title="Identité de la société"
              description="Nom et logo communs au tenant."
              icon={Buildings}
              testId="tenant-branding-settings"
            >
              <div className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(min(13rem,100%),1fr))] gap-4">
                <label>
                  <span className="text-xs font-semibold text-[var(--do-ink)]">
                    Nom de la société
                  </span>

                  <input
                    value={companyName}
                    onChange={(event) =>
                      setCompanyName(
                        event.target.value
                      )
                    }
                    className="mt-2 h-10 w-full rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-xs outline-none focus:border-[var(--do-accent)]"
                    data-testid="tenant-name-input"
                  />
                </label>

                <label>
                  <span className="text-xs font-semibold text-[var(--do-ink)]">
                    Logo
                  </span>

                  <div className="mt-2 flex h-10 items-center gap-2">
                    <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)]">
                      {logoUrl ? (
                        <img
                          src={`${API}${logoUrl}`}
                          alt="Logo société"
                          className="max-h-full max-w-full object-contain"
                        />
                      ) : (
                        <Buildings
                          size={18}
                        />
                      )}
                    </div>

                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      onChange={(event) =>
                        setLogoFile(
                          event.target.files?.[0] ||
                          null
                        )
                      }
                      className="min-w-0 text-[10px]"
                      data-testid="tenant-logo-input"
                    />
                  </div>
                </label>
              </div>

              <button
                type="button"
                onClick={saveBranding}
                disabled={saving}
                className="mt-5 inline-flex h-9 items-center gap-2 rounded-lg bg-[var(--do-blue)] px-4 text-xs font-heading font-bold text-[var(--do-on-accent)] disabled:opacity-50"
                data-testid="save-tenant-branding-btn"
              >
                <CheckCircle
                  size={16}
                  weight="fill"
                />

                Enregistrer l’identité
              </button>
            </SectionCard>
          )}
        </div>
      )}

      {activeTab ===
        "mail_account" &&
        canUseMail && (
        <MyMailSettings />
      )}

      {activeTab ===
        "secure_share" && (
        <ShareMessageSettings
          canManageTenant={
            canManageTenant
          }
        />
      )}

      {activeTab ===
        "security" && (
        <div
          className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(min(24rem,100%),1fr))] gap-4"
          data-testid="settings-security-panel"
        >
          <SectionCard
            title="Mot de passe"
            description="Modifiez le mot de passe du compte connecté."
            icon={Lock}
          >
            <div className="space-y-4">
              {[
                {
                  label:
                    "Mot de passe actuel",
                  value:
                    currentPassword,
                  setter:
                    setCurrentPassword,
                  autocomplete:
                    "current-password",
                },
                {
                  label:
                    "Nouveau mot de passe",
                  value:
                    newPassword,
                  setter:
                    setNewPassword,
                  autocomplete:
                    "new-password",
                },
                {
                  label:
                    "Confirmation",
                  value:
                    confirmPassword,
                  setter:
                    setConfirmPassword,
                  autocomplete:
                    "new-password",
                },
              ].map(
                (field) => (
                  <label
                    key={field.label}
                    className="block"
                  >
                    <span className="text-xs font-semibold text-[var(--do-ink)]">
                      {field.label}
                    </span>

                    <div className="relative mt-2">
                      <input
                        type={
                          showPasswords
                            ? "text"
                            : "password"
                        }
                        value={field.value}
                        autoComplete={
                          field.autocomplete
                        }
                        onChange={(event) =>
                          field.setter(
                            event.target.value
                          )
                        }
                        className="h-10 w-full rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 pr-10 text-xs outline-none focus:border-[var(--do-accent)]"
                      />

                      <button
                        type="button"
                        onClick={() =>
                          setShowPasswords(
                            (value) =>
                              !value
                          )
                        }
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--do-muted)]"
                      >
                        {showPasswords ? (
                          <EyeSlash
                            size={17}
                          />
                        ) : (
                          <Eye
                            size={17}
                          />
                        )}
                      </button>
                    </div>
                  </label>
                )
              )}
            </div>

            <button
              type="button"
              onClick={savePassword}
              disabled={saving}
              className="mt-5 inline-flex h-9 items-center gap-2 rounded-lg bg-[var(--do-blue)] px-4 text-xs font-heading font-bold text-[var(--do-on-accent)] disabled:opacity-50"
              data-testid="save-password-btn"
            >
              <Key
                size={16}
                weight="bold"
              />

              Mettre à jour
            </button>
          </SectionCard>

          <SectionCard
            title="Sécurité du compte"
            description="État des protections disponibles."
            icon={ShieldCheck}
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-lg border border-[var(--do-border)] p-3">
                <div>
                  <div className="text-xs font-semibold text-[var(--do-ink)]">
                    Verrouillage automatique
                  </div>

                  <div className="mt-1 text-[10px] text-[var(--do-muted)]">
                    Protection après plusieurs échecs de connexion.
                  </div>
                </div>

                <span className="rounded-full bg-[var(--do-accent-soft)] px-2 py-1 text-[9px] font-bold text-[var(--do-accent)]">
                  Actif
                </span>
              </div>

              <div className="flex items-center justify-between rounded-lg border border-[var(--do-border)] p-3">
                <div>
                  <div className="text-xs font-semibold text-[var(--do-ink)]">
                    Cookies sécurisés
                  </div>

                  <div className="mt-1 text-[10px] text-[var(--do-muted)]">
                    Session protégée par cookies HTTPOnly.
                  </div>
                </div>

                <span className="rounded-full bg-[var(--do-accent-soft)] px-2 py-1 text-[9px] font-bold text-[var(--do-accent)]">
                  Actif
                </span>
              </div>

              <div className="flex items-center justify-between rounded-lg border border-[var(--do-border)] p-3">
                <div>
                  <div className="text-xs font-semibold text-[var(--do-ink)]">
                    Double authentification
                  </div>

                  <div className="mt-1 text-[10px] text-[var(--do-muted)]">
                    Fonction non encore activée.
                  </div>
                </div>

                <span className="rounded-full bg-[var(--do-accent-soft)] px-2 py-1 text-[9px] font-bold text-[var(--do-accent)]">
                  À venir
                </span>
              </div>
            </div>
          </SectionCard>
        </div>
      )}

      {activeTab ===
        "notifications" && (
        <div
          data-testid="settings-notifications-panel"
        >
          <NotificationSettings
            embedded
          />
        </div>
      )}

      {activeTab ===
        "users" &&
        canManageUsers && (
        <div
          data-testid="settings-users-panel"
        >
          <Users
            embedded
          />
        </div>
      )}

      {activeTab ===
        "machine_catalog" &&
        canManageTenant && (
        <MachineCatalogSettings
          embedded
        />
      )}

    </div>
  );
}
